import {
  createEntityAdapter,
  createSlice,
  createAsyncThunk,
  createSelector
} from '@reduxjs/toolkit';
import axios from 'axios';

import {
  fetchGetCampaign,
  fetchGetCampaigns,
  fetchEditCampaign
} from 'api/campaignApi';
import { fetchGetCampaignsGraphQL } from 'api/graphql/campaignGraphQL';
import { fetchGetCampaignUsersGraphQL } from 'api/graphql/campaignGraphQL';
import {
  fetchGetCampaignKiosks,
  fetchGetCampaignBillingInfo
} from 'api/campaignDetailsApi';
import { assignPaymentMethodToCampaignGraphQL } from 'api/graphql/paymentMethodsGraphQL';
import {
  fetchActionCampaignArtChange,
  fetchAddComment,
  fetchGetCampaignArt,
  fetchRequestCampaignArtChange
} from 'api/campaignArtApi';
import { approveCustomerArtProofGraphQL, rejectCustomerArtProofGraphQL } from 'api/graphql/artProofReviewGraphQL';
import { fetchAddCampaignMessage, fetchGetCampaignMessages } from 'api/campaignMessagesApi';
import { fetchCampaignMessagesGraphQL, sendCampaignMessageGraphQL, markMessagesAsReadGraphQL } from 'api/graphql/campaignMessagesGraphQL';
import { 
  fetchCustomerCampaignArtGraphQL, 
  submitCustomerArtBriefGraphQL, 
  reviewCustomerArtProofGraphQL, 
  addCustomerArtNoteGraphQL,
  updateCustomerArtBriefGraphQL 
} from 'api/graphql/campaignArtGraphQL';
import { adminRoles, emptyCampaign } from 'constants/campaignConstants';
import { campaignArtStatuses, designArtStatuses } from 'constants/statuses';
import { parseArtDesignStatus } from 'services/campaignArtServices';
import { 
  parseCampaignArtFromGraphQL, 
  transformToArtBriefInput,
  transformToUpdateArtBriefInput 
} from 'services/campaignArtGraphQLServices';
import { parseCampaignMessageGraphQL } from 'services/campaignMessagesServices';
import showSnackbarError from 'utils/showSnackbarError';

const initialState = {
  totalCount: 0,
  totalPages: 0,
  pageInfo: { hasNextPage: false, hasPreviousPage: false, startCursor: null, endCursor: null },
  isLoading: false,
  isAllLoading: false,
  isMessagesLoading: false,
  isMessagesUpdating: false,
  isAdminsLoading: false,
  isKiosksLoading: false,
  isBillingLoading: false,
  artChangeRequests: []
};

const campaignsAdapter = createEntityAdapter({ selectId: (campaign) => campaign.id });

export const getCampaigns = createAsyncThunk(
  'campaigns/getCampaigns',
  async (params, { dispatch, rejectWithValue, signal }) => {
    try {
      // Use GraphQL with cursor-based pagination
      const { first, after } = params;
      
      const { campaigns, totalCount, pageInfo } = await fetchGetCampaignsGraphQL({
        first,
        after
      });

      return { campaigns, totalCount, pageInfo };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { isAllLoading } = getState().campaigns;

      if (isAllLoading) {
        return false;
      }

      return true;
    }
  }
);

export const getCampaign = createAsyncThunk(
  'campaigns/getCampaign',
  async ({ id, navigate }, { dispatch, getState, rejectWithValue }) => {
    try {
      const campaign = await fetchGetCampaign({ id });

      if (campaign) {
        // Fetch art data via GraphQL
        const campaignArt = await fetchCustomerCampaignArtGraphQL(id);
        const { designArts, artPreferences } = parseCampaignArtFromGraphQL(campaignArt);

        const {
          workflow,
          status,
          billingStatus,
          statusDescription
        } = campaign;

        const artStatus = parseArtDesignStatus(
          workflow,
          status,
          designArts,
          artPreferences,
          billingStatus,
          statusDescription
        );

        return {
          ...campaign,
          designArts,
          artPreferences,
          artStatus
        };
      }

      dispatch(showSnackbarError('You have no access to this campaign'));

      navigate('/campaigns');

      return campaign;
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { isLoading } = getState().campaigns;

      if (isLoading) {
        return false;
      }

      return true;
    }
  }
);

export const assignCampaignPaymentMethodGraphQL = createAsyncThunk(
  'campaigns/assignCampaignPaymentMethodGraphQL',
  async ({ campaignId, paymentMethodId }, { dispatch, rejectWithValue }) => {
    try {
      await assignPaymentMethodToCampaignGraphQL({
        campaignId,
        customerPaymentMethodId: paymentMethodId
      });

      return { 
        id: campaignId, 
        changes: { 
          paymentMethodId,
          paymentStatus: 2 // SUBMITTED
        } 
      };
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Failed to assign payment method';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  },
);

export const getCampaignArtGraphQL = createAsyncThunk(
  'campaigns/getCampaignArtGraphQL',
  async ({ campaignId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const campaignArt = await fetchCustomerCampaignArtGraphQL(campaignId);
      const { designArts, artPreferences } = parseCampaignArtFromGraphQL(campaignArt);

      return {
        id: campaignId,
        changes: {
          designArts,
          artPreferences
        }
      };
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Failed to fetch campaign art';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const submitArtBriefGraphQL = createAsyncThunk(
  'campaigns/submitArtBriefGraphQL',
  async ({ campaignId, artBriefData }, { dispatch, getState, rejectWithValue }) => {
    try {
      const input = transformToArtBriefInput(artBriefData, campaignId);
      const result = await submitCustomerArtBriefGraphQL(input);

      if (!result.success) {
        throw new Error(result.error || result.message || 'Failed to submit art brief');
      }

      // Re-fetch campaign art to get the updated data
      const campaignArt = await fetchCustomerCampaignArtGraphQL(campaignId);
      const { designArts, artPreferences } = parseCampaignArtFromGraphQL(campaignArt);

      return {
        id: campaignId,
        changes: {
          designArts,
          artPreferences,
          artStatus: 2 // ART_REQUIRED
        }
      };
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Failed to submit art brief';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateArtBriefGraphQL = createAsyncThunk(
  'campaigns/updateArtBriefGraphQL',
  async ({ campaignId, artBriefId, artBriefData }, { dispatch, getState, rejectWithValue }) => {
    try {
      const input = transformToUpdateArtBriefInput(artBriefData, campaignId, artBriefId);
      const result = await updateCustomerArtBriefGraphQL(input);

      if (!result) {
        throw new Error('Failed to update art brief');
      }

      // Re-fetch campaign art to get the updated data
      const campaignArt = await fetchCustomerCampaignArtGraphQL(campaignId);
      const { designArts, artPreferences } = parseCampaignArtFromGraphQL(campaignArt);

      return {
        id: campaignId,
        changes: {
          designArts,
          artPreferences
        }
      };
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Failed to update art brief';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const reviewArtProofGraphQL = createAsyncThunk(
  'campaigns/reviewArtProofGraphQL',
  async ({ campaignId, campaignArtBriefId, isApproved }, { dispatch, getState, rejectWithValue }) => {
    try {
      const result = await reviewCustomerArtProofGraphQL(campaignArtBriefId, isApproved);

      if (!result.success) {
        throw new Error(result.error || result.message || 'Failed to review art proof');
      }

      // Re-fetch campaign art to get the updated data
      const campaignArt = await fetchCustomerCampaignArtGraphQL(campaignId);
      const { designArts, artPreferences } = parseCampaignArtFromGraphQL(campaignArt);

      return {
        id: campaignId,
        changes: {
          designArts,
          artPreferences,
          artStatus: isApproved ? 6 : 4 // ART_LOCATION_SIGNED or ART_REJECTED
        }
      };
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Failed to review art proof';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const addCampaignPaymentMethod = createAsyncThunk(
  'campaigns/addCampaignPaymentMethod',
  async ({ id, paymentMethodId }, { dispatch, rejectWithValue }) => {
    try {
      const campaign = await fetchEditCampaign({ id, billing_info_id: paymentMethodId });

      return { id, changes: campaign };
    } catch (e) {
      const error = JSON.parse(e.message);

      if (error?.statusCode === 400 && error?.errors[0]) {
        const { auth_msg: authMsg } = JSON.parse(error.errors[0]);

        if (authMsg === 'A/DECLINED' || authMsg === 'Unknown') {
          dispatch(showSnackbarError('Invalid Card Data'));
        } else {
          dispatch(showSnackbarError(`Error message: ${authMsg}`));
        }
      } else {
        dispatch(showSnackbarError(e.message, e.stack));
      }

      return rejectWithValue(e.message);
    }
  }
);

export const addArtCommentGraphQL = createAsyncThunk(
  'campaigns/addArtCommentGraphQL',
  async ({ campaignId, postId, comment }, { getState, dispatch, rejectWithValue }) => {
    try {
      // Add comment via GraphQL
      const newNote = await addCustomerArtNoteGraphQL(postId, comment);

      const campaign =
        getState().campaigns.entities[campaignId] ||
        getState().campaigns.entities[Number(campaignId)] ||
        getState().campaigns.entities[String(campaignId)];
      const oldDesignArt = campaign?.designArts?.find(
        (el) => Number(el.id) === Number(postId)
      );
      if (!oldDesignArt) {
        return { id: campaignId, changes: {} };
      }

      const existingComments = oldDesignArt.comments || [];
      // SignalR CommentAdded may land before this fulfilled handler — avoid a duplicate flash.
      if (existingComments.some((c) => Number(c.id) === Number(newNote.id))) {
        return { id: campaignId, changes: { designArts: campaign.designArts } };
      }

      const newComment = {
        id: newNote.id,
        message: newNote.note,
        createdDate: newNote.createDate,
        username: newNote.senderType?.typeName || 'You',
        avatarColor: 'primary', // Client color
        avatarLetter: 'Y', // Client letter
        isMy: false // All comments align left
      };

      const updatedArt = {
        ...oldDesignArt,
        comments: [...existingComments, newComment]
      };

      const designArts = campaign.designArts.map((el) =>
        Number(el.id) === Number(postId) ? updatedArt : el
      );

      return { id: campaignId, changes: { designArts } };
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Failed to add comment';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const addArtComment = createAsyncThunk(
  'campaigns/addArtComment',
  async ({ campaignId, postId, comment }, { getState, dispatch, rejectWithValue }) => {
    const { email } = getState().auth.user;

    try {
      fetchAddComment({ campaignId, artId: postId, note: comment });

      const campaign = getState().campaigns.entities[campaignId];
      const oldDesignArt = campaign.designArts.find((el) => el.id === postId);
      const createdDate = Date.now();
      const newComment = {
        id: `${comment}${createdDate}`,
        message: comment,
        createdDate,
        username: email
      };

      const updatedArt = { ...oldDesignArt, comments: [...oldDesignArt.comments, newComment] };

      const designArts = campaign.designArts.map((el) => (el.id === postId ? updatedArt : el));

      return { id: campaignId, changes: { designArts } };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const getCampaignMessages = createAsyncThunk(
  'campaigns/getCampaignMessages',
  async ({ campaignId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { firstName, lastName } = getState().auth.user;

      // Use GraphQL API
      const messagesGraphQL = await fetchCampaignMessagesGraphQL(campaignId);
      
      // Construct full name to match backend's SenderUserName
      const fullName = `${firstName} ${lastName}`.trim();
      
      // Parse messages from GraphQL format
      const messages = messagesGraphQL.map((msg) => parseCampaignMessageGraphQL(msg, fullName));

      // Mark unread messages from Terraboost (senderType: 145) as read
      const unreadTerraboostMessages = messagesGraphQL
        .filter(msg => msg.senderType === 145 && !msg.messageRead)
        .map(msg => msg.id);

      if (unreadTerraboostMessages.length > 0) {
        // Fire and forget - don't wait for this to complete
        markMessagesAsReadGraphQL(unreadTerraboostMessages).catch(err => 
          console.error('Failed to mark messages as read:', err)
        );
      }

      // Messages come DESC from GraphQL, reverse for ascending order
      return { id: campaignId, changes: { messages: messages.reverse() } };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { isMessagesLoading } = getState().campaigns;

      if (isMessagesLoading) {
        return false;
      }

      return true;
    }
  }
);

export const updateCampaignMessages = createAsyncThunk(
  'campaigns/updateCampaignMessages',
  async ({ campaignId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const { firstName, lastName } = getState().auth.user;

      // Use GraphQL API
      const messagesGraphQL = await fetchCampaignMessagesGraphQL(campaignId);
      
      // Construct full name to match backend's SenderUserName
      const fullName = `${firstName} ${lastName}`.trim();
      
      // Parse messages from GraphQL format
      const messages = messagesGraphQL.map((msg) => parseCampaignMessageGraphQL(msg, fullName));

      // Mark unread messages from Terraboost (senderType: 145) as read
      const unreadTerraboostMessages = messagesGraphQL
        .filter(msg => msg.senderType === 145 && !msg.messageRead)
        .map(msg => msg.id);

      if (unreadTerraboostMessages.length > 0) {
        // Fire and forget - don't wait for this to complete
        markMessagesAsReadGraphQL(unreadTerraboostMessages).catch(err => 
          console.error('Failed to mark messages as read:', err)
        );
      }

      // Messages come DESC from GraphQL, reverse for ascending order
      return { id: campaignId, changes: { messages: messages.reverse() } };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.response?.data || e.message);
    }
  }
);

export const addCampaignMessage = createAsyncThunk(
  'campaigns/addCampaignMessage',
  async ({ campaignId, message, attachmentUrl }, { getState, dispatch, rejectWithValue }) => {
    try {
      const { firstName, lastName, username } = getState().auth.user;
      const { messages } = getState().campaigns.entities[campaignId];

      // Use GraphQL mutation
      const newMessageGraphQL = await sendCampaignMessageGraphQL(campaignId, message, attachmentUrl);
      
      // Construct full name to match backend's SenderUserName
      const fullName = `${firstName} ${lastName}`.trim();
      
      // Parse message from GraphQL format with full name for isMy flag
      const newMessage = parseCampaignMessageGraphQL(newMessageGraphQL, fullName);

      return { id: campaignId, changes: { messages: [...messages, newMessage] } };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const getCampaignAdmins = createAsyncThunk(
  'campaigns/getCampaignAdmins',
  async ({ campaignId }, { dispatch, rejectWithValue }) => {
    try {
      const campaignUsers = await fetchGetCampaignUsersGraphQL({ campaignId });

      const campaignAdmins = {};

      // Map GraphQL role types to admin role constants
      const roleMapping = {
        SalesRep: 'SALES_REP',
        ArtDesigner: adminRoles.ART,
        MarketingSpecialist: adminRoles.CS,
        CustomerCare: adminRoles.CUSTOMER_CARE,
        Telemarketer: 'TELEMARKETER'
      };

      campaignUsers.forEach((cu) => {
        const mappedRole = roleMapping[cu.campaignRoleType];
        if (mappedRole && cu.user) {
          campaignAdmins[mappedRole] = {
            id: cu.user.id,
            name: `${cu.user.firstName} ${cu.user.lastName}`,
            email: cu.user.email,
            phone: cu.user.phone,
            role: mappedRole
          };
        }
      });

      console.log('Campaign Admins being stored:', campaignAdmins);
      return { id: campaignId, changes: { admins: campaignAdmins } };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));

      return rejectWithValue(e.message);
    }
  }
);

export const getCampaignKiosks = createAsyncThunk(
  'campaigns/getCampaignKiosks',
  async ({ campaignId, paginationOptions }, { dispatch, rejectWithValue }) => {
    try {
      const { kiosks, totalCount, pageInfo } = await fetchGetCampaignKiosks(campaignId, paginationOptions);

      return {
        id: campaignId,
        changes: {
          kiosks,
          kiosksTotalCount: totalCount,
          kiosksPageInfo: pageInfo
        }
      };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));
      return rejectWithValue(e.message);
    }
  }
);

export const getCampaignBillingInfo = createAsyncThunk(
  'campaigns/getCampaignBillingInfo',
  async ({ campaignId }, { dispatch, getState, rejectWithValue }) => {
    try {
      const billingInfo = await fetchGetCampaignBillingInfo(campaignId);
      
      // Get campaignDuration from the campaign object (already loaded)
      const campaign = getState().campaigns.entities[campaignId];
      const campaignDuration = campaign?.campaignDuration;

      return {
        id: campaignId,
        changes: {
          payment: {
            ...billingInfo,
            campaignDuration
          }
        }
      };
    } catch (e) {
      dispatch(showSnackbarError(e.message, e.stack));
      return rejectWithValue(e.message);
    }
  }
);

export const approveCampaignArt = createAsyncThunk(
  'campaigns/approveCampaignArt',
  async ({ postId, campaignId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const oldCampaign = getState().campaigns.entities[campaignId];

      // Call GraphQL mutation to approve art proof
      await approveCustomerArtProofGraphQL(postId);

      // Refetch campaign art to get updated status
      const artData = await fetchCustomerCampaignArtGraphQL(campaignId);
      const { designArts, artPreferences } = parseCampaignArtFromGraphQL(artData);

      return {
        id: campaignId,
        changes: {
          designArts,
          artPreferences
        }
      };
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Failed to approve art proof';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const rejectCampaignArt = createAsyncThunk(
  'campaigns/rejectCampaignArt',
  async ({ postId, campaignId, reason }, { getState, dispatch, rejectWithValue }) => {
    try {
      // Call GraphQL mutation to reject art proof with reason
      await rejectCustomerArtProofGraphQL(postId, reason);

      // Refetch campaign art to get updated status and rejection note
      const artData = await fetchCustomerCampaignArtGraphQL(campaignId);
      const { designArts, artPreferences } = parseCampaignArtFromGraphQL(artData);

      return {
        id: campaignId,
        changes: {
          designArts,
          artPreferences
        }
      };
    } catch (e) {
      const errorMessage = e.graphQLErrors?.[0]?.message || e.message || 'Failed to reject art proof';
      dispatch(showSnackbarError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const requestArtChange = createAsyncThunk(
  'campaigns/requestArtChange',
  async ({ campaignId, goal, files }, { getState, rejectWithValue }) => {
    try {
      const { artChangeRequests } = getState().campaigns.entities[campaignId];

      const result = await fetchRequestCampaignArtChange({
        campaign_id: campaignId,
        description: goal,
        attachments: files
      });

      return { id: campaignId, changes: { artChangeRequests: [...artChangeRequests, result] } };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const submitArtChangeQuote = createAsyncThunk(
  'campaigns/submitArtChangeQuote',
  async ({ comment, artChangeId, campaignId, action }, { getState, rejectWithValue }) => {
    try {
      const { artChangeRequests } = getState().campaigns.entities[campaignId];

      const result = await fetchActionCampaignArtChange({
        id: artChangeId,
        action,
        reason: comment
      });

      return {
        id: campaignId,
        changes: {
          artChangeRequests: artChangeRequests.map((el) => (el.id === artChangeId ? result : el))
        }
      };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState: campaignsAdapter.getInitialState(initialState),
  reducers: {
    updateCampaign(state, { payload }) {
      campaignsAdapter.setOne(state, payload);
    },
    updateCampaignFields(state, { payload: { id, changes } }) {
      campaignsAdapter.updateOne(state, { id, changes });
    },
    /**
     * Apply a CommentAdded SignalR event to designArts immediately.
     * Used when management (View Art Design / Design Coordination) posts a
     * customer-visible note so the customer portal updates without waiting on refetch.
     */
    applyRealtimeArtComment(state, { payload }) {
      const { campaignId, artFileId, comment, commenterName } = payload || {};
      if (campaignId == null || artFileId == null || comment?.id == null) return;
      if (comment.internalNote) return;

      const entityId = [campaignId, Number(campaignId), String(campaignId)].find(
        (key) => state.entities[key]
      );
      if (entityId == null) return;

      const campaign = state.entities[entityId];
      if (!Array.isArray(campaign?.designArts) || !campaign.designArts.length) return;

      const senderTypeId = Number(comment.senderTypeId);
      const isClient = senderTypeId === 160;
      const isTerraboost = senderTypeId === 161;
      const typeName = comment.senderType?.typeName ?? comment.senderType?.TypeName;
      const newComment = {
        id: comment.id,
        message: comment.note ?? comment.message ?? '',
        createdDate: comment.createDate ?? comment.CreateDate,
        username: isClient
          ? 'You'
          : typeName || commenterName || (isTerraboost ? 'Terraboost' : 'User'),
        avatarColor: isClient ? 'primary' : isTerraboost ? 'secondary' : 'grey',
        avatarLetter: isClient ? 'Y' : isTerraboost ? 'T' : 'U',
        isMy: false
      };

      const designArts = campaign.designArts.map((art) => {
        if (Number(art.id) !== Number(artFileId)) return art;
        const comments = art.comments || [];
        if (comments.some((c) => Number(c.id) === Number(newComment.id))) {
          return art;
        }
        return { ...art, comments: [...comments, newComment] };
      });

      campaignsAdapter.updateOne(state, { id: entityId, changes: { designArts } });
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCampaigns.pending, (state) => {
        state.isAllLoading = true;
      })
      .addCase(getCampaigns.fulfilled, (state, { payload }) => {
        campaignsAdapter.setAll(state, payload.campaigns);
        state.totalCount = payload.totalCount;
        state.pageInfo = payload.pageInfo || initialState.pageInfo;
        state.isAllLoading = false;
      })
      .addCase(getCampaigns.rejected, (state) => {
        state.isAllLoading = false;
      })
      .addCase(getCampaign.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCampaign.fulfilled, (state, action) => {
        if (action.payload) {
          campaignsAdapter.upsertOne(state, action.payload);
        }

        state.isLoading = false;
      })
      .addCase(getCampaign.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(assignCampaignPaymentMethodGraphQL.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(assignCampaignPaymentMethodGraphQL.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(assignCampaignPaymentMethodGraphQL.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getCampaignArtGraphQL.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.isLoading = true;
        }
      })
      .addCase(getCampaignArtGraphQL.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(getCampaignArtGraphQL.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(submitArtBriefGraphQL.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(submitArtBriefGraphQL.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(submitArtBriefGraphQL.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(updateArtBriefGraphQL.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateArtBriefGraphQL.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(updateArtBriefGraphQL.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(reviewArtProofGraphQL.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(reviewArtProofGraphQL.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(reviewArtProofGraphQL.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(addCampaignPaymentMethod.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addCampaignPaymentMethod.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(addCampaignPaymentMethod.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getCampaignMessages.pending, (state) => {
        state.isMessagesLoading = true;
      })
      .addCase(getCampaignMessages.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isMessagesLoading = false;
      })
      .addCase(getCampaignMessages.rejected, (state) => {
        state.isMessagesLoading = false;
      })
      .addCase(updateCampaignMessages.pending, (state) => {
        state.isMessagesUpdating = true;
      })
      .addCase(updateCampaignMessages.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isMessagesUpdating = false;
      })
      .addCase(updateCampaignMessages.rejected, (state) => {
        state.isMessagesUpdating = false;
      })
      .addCase(addCampaignMessage.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(addArtComment.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(addArtCommentGraphQL.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addArtCommentGraphQL.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isLoading = false;
      })
      .addCase(addArtCommentGraphQL.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getCampaignAdmins.pending, (state) => {
        state.isAdminsLoading = true;
      })
      .addCase(getCampaignAdmins.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isAdminsLoading = false;
      })
      .addCase(getCampaignAdmins.rejected, (state) => {
        state.isAdminsLoading = false;
        state.isAdminsLoaded = true;
      })
      .addCase(getCampaignKiosks.pending, (state) => {
        state.isKiosksLoading = true;
      })
      .addCase(getCampaignKiosks.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isKiosksLoading = false;
      })
      .addCase(getCampaignKiosks.rejected, (state) => {
        state.isKiosksLoading = false;
      })
      .addCase(getCampaignBillingInfo.pending, (state) => {
        state.isBillingLoading = true;
      })
      .addCase(getCampaignBillingInfo.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
        state.isBillingLoading = false;
      })
      .addCase(getCampaignBillingInfo.rejected, (state) => {
        state.isBillingLoading = false;
      })
      .addCase(rejectCampaignArt.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(approveCampaignArt.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(requestArtChange.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      })
      .addCase(submitArtChangeQuote.fulfilled, (state, action) => {
        campaignsAdapter.updateOne(state, action.payload);
      });
  }
});

const { selectAll, selectById } = campaignsAdapter.getSelectors((state) => state.campaigns);

const all = createSelector(selectAll, (state) => state);

const byId = (id) =>
  createSelector(
    (state) => state,
    (state) => selectById(state, id) || emptyCampaign
  );

const selectSelf = (state) => state.campaigns;
const isAllLoading = createSelector(selectSelf, (state) => state.isAllLoading);
const isLoading = createSelector(selectSelf, (state) => state.isLoading);
const isMessagesLoading = createSelector(selectSelf, (state) => state.isMessagesLoading);
const isMessagesUpdating = createSelector(selectSelf, (state) => state.isMessagesUpdating);
const isAdminsLoading = createSelector(selectSelf, (state) => state.isAdminsLoading);
const isKiosksLoading = createSelector(selectSelf, (state) => state.isKiosksLoading);
const isBillingLoading = createSelector(selectSelf, (state) => state.isBillingLoading);
const totalCount = createSelector(selectSelf, (state) => state.totalCount);
const totalPages = createSelector(selectSelf, (state) => state.totalPages);
const pageInfo = createSelector(selectSelf, (state) => state.pageInfo);
const artChangeRequests = createSelector(selectSelf, (state) => state.artChangeRequests);

export const CampaignsSelectors = {
  all,
  byId,
  isAllLoading,
  isLoading,
  isMessagesLoading,
  isMessagesUpdating,
  isAdminsLoading,
  isKiosksLoading,
  isBillingLoading,
  totalCount,
  totalPages,
  pageInfo,
  artChangeRequests
};

export const { updateCampaign, updateCampaignFields, applyRealtimeArtComment } =
  campaignsSlice.actions;

export default campaignsSlice.reducer;
